// ============================================================================
// What a file is, for the Files app (docs/areas/files.md): which viewer draws
// it, what to call its type, how big it reads. Pure and tested (types.test.ts).
// Everything is decided from the name and the stored content type, never by
// sniffing bytes in the page: the viewers that read bytes (text, JSON, CSV,
// zip, fonts) treat them as data, and nothing is ever put in the DOM as markup.
// ============================================================================

export type ViewerKind =
  | 'image'
  | 'svg'
  | 'video'
  | 'audio'
  | 'pdf'
  | 'markdown'
  | 'json'
  | 'csv'
  | 'code'
  | 'text'
  | 'zip'
  | 'font'
  | 'none'

/** Text the browser parses is capped: past this a viewer shows the start and says so. */
export const TEXT_MAX_BYTES = 2 * 1024 * 1024
/** JSON is parsed whole, so it has its own cap. */
export const JSON_MAX_BYTES = 5 * 1024 * 1024
/** Highlighting stops past this many lines (the lexer's own guard is similar). */
export const CODE_MAX_LINES = 5000
/** A CSV shows at most this many rows. */
export const CSV_MAX_ROWS = 500
/** A font is read whole to draw its specimen. */
export const FONT_MAX_BYTES = 20 * 1024 * 1024

export const extOf = (name: string): string => {
  const dot = name.lastIndexOf('.')
  return dot > 0 ? name.slice(dot + 1).toLowerCase() : ''
}

const IMAGE = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'avif', 'bmp', 'ico', 'heic', 'heif'])
const VIDEO = new Set(['mp4', 'm4v', 'webm', 'mov', 'ogv'])
const AUDIO = new Set(['mp3', 'm4a', 'aac', 'wav', 'ogg', 'oga', 'opus', 'flac', 'weba'])
const FONT = new Set(['ttf', 'otf', 'woff', 'woff2'])
const MARKDOWN = new Set(['md', 'markdown', 'mdx'])
const CSV = new Set(['csv', 'tsv'])

/** File extension → the fence word the app's lexer knows (components/Code.tsx); others stay plain. */
export const CODE_LANG: Record<string, string> = {
  ts: 'ts',
  tsx: 'tsx',
  mts: 'ts',
  cts: 'ts',
  js: 'js',
  jsx: 'jsx',
  mjs: 'js',
  cjs: 'js',
  go: 'go',
  css: 'css',
  scss: 'css',
  sql: 'sql',
  yaml: 'yaml',
  yml: 'yaml',
  sh: 'sh',
  bash: 'sh',
  zsh: 'sh',
  py: 'py',
  rs: 'rs',
  json: 'json',
}

/** Code the lexer does not colour still reads best as code: monospace, line by line. */
const PLAIN_CODE = new Set([
  'c',
  'h',
  'cc',
  'cpp',
  'hpp',
  'ino',
  'java',
  'kt',
  'swift',
  'rb',
  'php',
  'lua',
  'toml',
  'ini',
  'conf',
  'cfg',
  'env',
  'xml',
  'html',
  'htm',
  'vue',
  'svelte',
  'astro',
  'dockerfile',
  'makefile',
  'gradle',
  'tf',
  'hcl',
  'nix',
  'proto',
  'graphql',
  'diff',
  'patch',
])
const TEXT = new Set(['txt', 'log', 'text', 'rst', 'adoc', 'srt', 'vtt'])
const NAMES_AS_CODE = new Set(['dockerfile', 'makefile', 'justfile', 'containerfile', '.gitignore', '.env'])

/**
 * Which viewer draws a file. The extension decides first (uploads from a phone
 * often carry an empty or generic type), then the content type. SVG is its own
 * kind: drawn as a picture (an <img>, which never runs its scripts), never put
 * in the page. HTML is code to read, never a page to render.
 */
export const viewerFor = (name: string, contentType = ''): ViewerKind => {
  const ext = extOf(name)
  const type = contentType.split(';')[0]!.trim().toLowerCase()
  if (ext === 'svg' || type === 'image/svg+xml') return 'svg'
  if (IMAGE.has(ext)) return 'image'
  if (VIDEO.has(ext)) return 'video'
  if (AUDIO.has(ext)) return 'audio'
  if (ext === 'pdf') return 'pdf'
  if (MARKDOWN.has(ext)) return 'markdown'
  if (ext === 'json' || ext === 'jsonc' || ext === 'geojson') return 'json'
  if (CSV.has(ext)) return 'csv'
  if (ext === 'zip') return 'zip'
  if (FONT.has(ext)) return 'font'
  if (CODE_LANG[ext] || PLAIN_CODE.has(ext) || NAMES_AS_CODE.has(name.toLowerCase())) return 'code'
  if (TEXT.has(ext)) return 'text'
  // no extension we know: the stored type
  if (type.startsWith('image/')) return 'image'
  if (type.startsWith('video/')) return 'video'
  if (type.startsWith('audio/')) return 'audio'
  if (type === 'application/pdf') return 'pdf'
  if (type === 'application/json') return 'json'
  if (type === 'text/csv' || type === 'text/tab-separated-values') return 'csv'
  if (type === 'text/markdown') return 'markdown'
  if (type === 'application/zip' || type === 'application/x-zip-compressed') return 'zip'
  if (type.startsWith('font/')) return 'font'
  if (type.startsWith('text/')) return 'text'
  return 'none'
}

/** Whether a viewer reads the bytes with fetch() (and so has a size cap), rather than handing a URL to the browser. */
export const readsBytes = (v: ViewerKind): boolean =>
  v === 'markdown' || v === 'json' || v === 'csv' || v === 'code' || v === 'text' || v === 'font'

/** The cap a viewer reads up to, or Infinity. */
export const capFor = (v: ViewerKind): number =>
  v === 'json' ? JSON_MAX_BYTES : v === 'font' ? FONT_MAX_BYTES : readsBytes(v) ? TEXT_MAX_BYTES : Infinity

/** A picture we can draw a thumbnail of on the device at upload. */
export const thumbable = (name: string, type: string): boolean => viewerFor(name, type) === 'image'

/** A type for an upload whose browser gave none. */
export const guessType = (name: string, given = ''): string => {
  if (given) return given
  const ext = extOf(name)
  const t: Record<string, string> = {
    jpg: 'image/jpeg',
    jpeg: 'image/jpeg',
    png: 'image/png',
    gif: 'image/gif',
    webp: 'image/webp',
    avif: 'image/avif',
    heic: 'image/heic',
    svg: 'image/svg+xml',
    mp4: 'video/mp4',
    mov: 'video/quicktime',
    webm: 'video/webm',
    mp3: 'audio/mpeg',
    m4a: 'audio/mp4',
    wav: 'audio/wav',
    flac: 'audio/flac',
    ogg: 'audio/ogg',
    pdf: 'application/pdf',
    zip: 'application/zip',
    json: 'application/json',
    csv: 'text/csv',
    md: 'text/markdown',
    txt: 'text/plain',
  }
  return t[ext] ?? 'application/octet-stream'
}

/** "2.4 MB": binary steps, one decimal under 10. */
export const formatSize = (bytes: number | undefined): string => {
  if (bytes === undefined || !Number.isFinite(bytes)) return ''
  if (bytes < 1024) return `${bytes} B`
  const units = ['KB', 'MB', 'GB', 'TB']
  let v = bytes / 1024
  let i = 0
  while (v >= 1024 && i < units.length - 1) {
    v /= 1024
    i++
  }
  return `${v < 10 ? v.toFixed(1) : Math.round(v)} ${units[i]}`
}

/** What a file's type is called in its details. */
export const typeLabel = (name: string, contentType = ''): string => {
  const v = viewerFor(name, contentType)
  const ext = extOf(name).toUpperCase()
  const words: Record<ViewerKind, string> = {
    image: 'Picture',
    svg: 'SVG picture',
    video: 'Video',
    audio: 'Audio',
    pdf: 'PDF',
    markdown: 'Markdown',
    json: 'JSON',
    csv: ext === 'TSV' ? 'TSV table' : 'CSV table',
    code: ext ? `${ext} code` : 'Code',
    text: 'Text',
    zip: 'Zip archive',
    font: 'Font',
    none: ext ? `${ext} file` : 'File',
  }
  return words[v]
}
